import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import { ModalInner } from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import TextInput from '@components/dataEntry/Input/TextInput';
import AutoTextarea from '@components/dataEntry/textarea/AutoTextarea';
import Button from '@components/dataEntry/button/Button';
import { Select, ISelectItem } from '@components/dataEntry/select';
import CustomUploadBox from '@components/dataEntry/upload/CustomUploadBox';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { ExplorationArea } from '@typings/worldmap/explorationArea.interface';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { IQuest, QuestType, RewardType } from '@typings/quest/questTypes';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import { representativePhotocardRefState } from '@atom/setting/homeSettingState';
import { updateQuest } from '@store/firebase/quest/update-quest';
import { deleteItemsFromInventory, getItemData } from '@store/firebase/store/parchase-item';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { updateStoreItem } from '@store/firebase/store/update-item';
import { QuestAcceptConditionType, QuestCompletionType } from '@typings/quest/questTypes';
import { addMasterGroupNotification } from '@store/firebase/notification/add-notification';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { User } from '@typings/setting/userType.interface';

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 760px;

  @media (max-width: 820px) {
    min-width: 100%;
  }
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const QuestSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--cardLineColor);
  border-radius: var(--cardBorderRadius);
`;

const RewardSection = styled(QuestSection)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuestNotice = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--subTextColor);
  line-height: 1.5;
`;

const rewardItemFunctionOptions: ISelectItem[] = [
  { key: ITEM_FUNCTION_TPYE.DEFAULT, value: '기본' },
  { key: ITEM_FUNCTION_TPYE.MYROOM, value: '마이룸 꾸미기용' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  photocardData?: IPhotoCard;
  targetItemData?: IstoreItem | any;
};

function UseQuestCreateItemModal({
  open,
  onClose,
  photocardData,
  targetItemData,
}: Props) {
  const currentUserID = useRecoilValue(currentUserIDState);
  const representativePhotocard = useRecoilValue(mainPhotocardDataState);
  const representativePhotocardRef = useRecoilValue(representativePhotocardRefState);
  const { data: currentUserData } = useDocument<User>(
    currentUserID ? `${CollectionPath.USERS}/${currentUserID}` : null,
    { listen: true },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [rewardTitle, setRewardTitle] = useState('');
  const [rewardDescription, setRewardDescription] = useState('');
  const [rewardContents, setRewardContents] = useState('');
  const [rewardFunctionType, setRewardFunctionType] = useState<string>(
    ITEM_FUNCTION_TPYE.DEFAULT,
  );
  const [rewardImageFile, setRewardImageFile] = useState<File | null>(null);

  const { data: visibleAreas } = useCollection<ExplorationArea>(
    CollectionPath.WORLDMAP,
    {
      listen: true,
      where: ['isVisible', '==', true],
    },
  );

  const areaOptions: ISelectItem[] = useMemo(() => {
    const options: ISelectItem[] = [{ value: '지역 없음', key: '' }];
    (visibleAreas || [])
      .filter((area) => area.id && area.id !== 'worldmapSetting')
      .forEach((area) => {
        options.push({ value: area.name, key: area.id || '' });
      });
    return options;
  }, [visibleAreas]);

  const isRequiredFilled = useMemo(() => {
    return !!title.trim() && !!rewardTitle.trim() && !!rewardImageFile;
  }, [title, rewardTitle, rewardImageFile]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      alert('퀘스트 제목을 입력해주세요.');
      return;
    }
    if (!rewardTitle.trim()) {
      alert('보상 아이템 제목을 입력해주세요.');
      return;
    }
    if (!rewardImageFile) {
      alert('보상 아이템 이미지를 등록해주세요.');
      return;
    }
    if (!currentUserID) {
      alert('로그인 정보가 없어 퀘스트를 만들 수 없습니다.');
      return;
    }

    setIsSubmitting(true);
    try {
      // 1) 보상 아이템 먼저 생성 (아이템 추가 로직 기본값 기반)
      const newStoreItem: IstoreItem = {
        title: rewardTitle.trim(),
        description: rewardDescription.trim(),
        contents: rewardContents.trim(),
        imgURL: '',
        functionType:
          rewardFunctionType === ITEM_FUNCTION_TPYE.MYROOM
            ? ITEM_FUNCTION_TPYE.MYROOM
            : ITEM_FUNCTION_TPYE.DEFAULT,
        price: 100000,
        isShow: false,
        order: 0,
        hashtagIdList: [],
        createdAt: new Date(),
      };
      const createdItemId = await updateStoreItem({
        data: newStoreItem,
        docID: '',
        hashtag: [],
        prevHashtag: [],
        fileList: {
          selectedFile: rewardImageFile,
          prevImgURL: '',
        },
      });
      if (!createdItemId) {
        throw new Error('보상 아이템 생성에 실패했습니다.');
      }

      const createdItemData = await getItemData({ itemID: createdItemId });
      const rewardItemData: Partial<IstoreItem> = {
        id: createdItemId,
        title: createdItemData?.title || newStoreItem.title,
        description: createdItemData?.description || newStoreItem.description,
        contents: createdItemData?.contents || newStoreItem.contents,
        imgURL: createdItemData?.imgURL || '',
        functionType: newStoreItem.functionType,
        price: 100000,
        isShow: false,
        order: 0,
        hashtagIdList: [],
      };

      const requestedBy =
        representativePhotocard?.id
          ? {
            key: representativePhotocard.id,
            path:
              representativePhotocardRef?.collectionPath ||
              representativePhotocard.path ||
              CollectionPath.PHOTOCARD,
            thumbnail: representativePhotocard.thumbnail || '',
            value: representativePhotocard.title || '',
          }
          : undefined;

      const newQuest: Partial<IQuest> = {
        type: QuestType.MEMBER_REQUEST,
        title: title.trim(),
        subTitle: subTitle.trim(),
        description: description.trim(),
        location: location || '',
        requirements: [],
        acceptCondition: {
          type: QuestAcceptConditionType.NONE,
          itemConditions: [],
          statConditions: [],
          failMessage: '조건을 충족하지 않아 퀘스트를 수락할 수 없습니다.',
        },
        completionCondition: {
          type: QuestCompletionType.LOG_COUNT,
          logCount: 1,
          itemIds: [],
          statConditions: [],
          enableLogRandomReward: true,
        },
        isRandomReward: true,
        rewards: [
          {
            type: RewardType.ITEM,
            amount: 1,
            name: rewardTitle.trim(),
            itemId: createdItemId,
            itemIds: [createdItemId],
            itemData: rewardItemData,
          },
        ],
        difficulty: 1,
        priority: 0,
        createdBy: currentUserID,
        isActive: false,
        requestedBy,
      };

      await updateQuest({
        data: newQuest,
        collectionPath: CollectionPath.QUEST,
      });

      const senderName =
        currentUserData?.displayName ||
        representativePhotocard?.title ||
        photocardData?.title ||
        '알 수 없는 유저';
      const questTitle = title.trim();

      const adminNotification = {
        type: NOTI_TYPE.QUEST_REQUEST,
        title: '퀘스트 검토 요청',
        contents: `${senderName}이(가) ${questTitle} 퀘스트를 생성하고 검토를 요청했습니다.`,
        sender: currentUserID,
        receiver: null,
        createdAt: new Date(),
        collectionPath: CollectionPath.SETTING,
        postId: 'quest',
        is_seen: false,
        targetPath: '/setting/quest?questFilter=memberRequest',
      };

      try {
        await Promise.all(addMasterGroupNotification({ data: adminNotification }) as any);
      } catch (notificationError) {
        console.error('관리자 알림 발송 실패:', notificationError);
      }

      if (targetItemData && photocardData?.id) {
        const inventoryPath = `${(photocardData as any)?.path || CollectionPath.PHOTOCARD}/${photocardData.id}/inventory`;
        await deleteItemsFromInventory({
          data: targetItemData,
          amount: 1,
          collectionPath: inventoryPath,
        });
      }

      setIsSubmitting(false);
      onClose();
      alert('퀘스트 등록이 완료되었습니다!\n관리자의 승인을 기다려 주세요.');
      return;
    } catch (error) {
      console.error('퀘스트 생성 실패:', error);
      alert('퀘스트 생성 중 오류가 발생했습니다.');
      setIsSubmitting(false);
    }
  }, [
    title,
    subTitle,
    description,
    location,
    rewardTitle,
    rewardDescription,
    rewardContents,
    rewardFunctionType,
    rewardImageFile,
    currentUserID,
    representativePhotocard,
    representativePhotocardRef,
    currentUserData,
    targetItemData,
    photocardData,
    onClose,
  ]);

  if (!open) return null;

  return (
    <ModalWrapper outsideClick={() => { }} contentStyle={{ minWidth: 860 }}>
      <ModalInner onClick={(e) => e.stopPropagation()}>
        <CloseBtn fixed onClick={onClose} size="md" />
        <FormWrap>
          <h6>퀘스트 만들기</h6>
          <SplitGrid>
            <QuestSection>
              <h6>퀘스트</h6>
              <TextInput
                label="퀘스트 제목"
                required
                value={title}
                name="title"
                onChange={(e) => setTitle(e.target.value)}
                placeholder="퀘스트 제목 입력"
              />
              <TextInput
                label="퀘스트 부제목"
                value={subTitle}
                name="subTitle"
                onChange={(e) => setSubTitle(e.target.value)}
                placeholder="퀘스트 부제목 입력"
              />
              <Select
                label="퀘스트 지역"
                selectList={areaOptions}
                selectedKey={location || ''}
                setSelectedKey={(key: string) => setLocation(key)}
                dropdownStyle={{ width: '100%' }}
              />
              <AutoTextarea
                label="퀘스트 설명"
                value={description}
                name="description"
                onChange={(e) => setDescription(e.target.value)}
                placeholder="퀘스트 설명을 입력하세요"
                minRows={4}
                maxLength={3000}
              />
              <QuestNotice>
                멤버 의뢰 퀘스트는 기본 완료 조건이 '로그 1개' 업로드 입니다.
              </QuestNotice>
            </QuestSection>

            <RewardSection>
              <h6>보상 아이템 설정</h6>
              <CustomUploadBox
                label="보상 아이템 이미지"
                required
                id="quest-reward-item-image"
                setSelectedFile={setRewardImageFile}
                prevImgURL=""
                text="보상 이미지 업로드"
                minHeight={40}
                style={{ height: '60px' }}
              />
              <TextInput
                label="보상 아이템 이름"
                required
                value={rewardTitle}
                name="rewardTitle"
                onChange={(e) => setRewardTitle(e.target.value)}
                placeholder="보상 아이템 이름"
              />
              <Select
                label="아이템 속성"
                selectList={rewardItemFunctionOptions}
                selectedKey={rewardFunctionType}
                setSelectedKey={(key: string) => setRewardFunctionType(key)}
                dropdownStyle={{ width: '100%' }}
              />
              <TextInput
                label="아이템 간략 설명"
                value={rewardDescription}
                name="rewardDescription"
                onChange={(e) => setRewardDescription(e.target.value)}
                placeholder="아이템 간략 설명"
              />
              <AutoTextarea
                label="아이템 상세 설명"
                value={rewardContents}
                name="rewardContents"
                onChange={(e) => setRewardContents(e.target.value)}
                placeholder="아이템 상세 설명"
                minRows={2}
                maxLength={1000}
              />
            </RewardSection>
          </SplitGrid>
          <Button
            onClick={handleSubmit}
            pending={isSubmitting}
            disabled={isSubmitting || !isRequiredFilled}
            block
          >
            퀘스트 생성하기
          </Button>
        </FormWrap>
      </ModalInner>
    </ModalWrapper>
  );
}

export default UseQuestCreateItemModal;
